/**
 * ProjectViewsModal.jsx
 *
 * Modal dialog component for managing project views.
 * Allows adding, selecting, editing, and deleting views within a project.
 * Supports Kanban and Task view types with customizable settings.
 * 
 * @component
 */

import { cloneDeep } from "lodash";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, InputAdornment, Paper, Stack, Typography } from "@mui/material";
import { ConfirmModal, GrayButton, PrimaryButton, TextField } from "../../common";
import { createStandardColumns, ColumnTypes, StandardColumnIds } from "../helpers/columnUtils";
import { default as ProjectViewUtils, ProjectViewType } from "../ProjectViews/projectViewUtils";
import AvailableViews from "./AvailableViews/AvailableViews";
import KanbanViewEditor from "./KanbanViewEditor/KanbanViewEditor";
import SelectedViews from "./SelectedViews/SelectedViews";
import TaskColumnsEditor from "./TaskColumnsEditor/TaskColumnsEditor";

/**
 * Builds a column configuration for a view, marking selected columns and sorting
 * them by static/dynamic type and reorderability.
 *
 * @param {Array} fieldIds - List of selected field IDs.
 * @param {Array} selectableColumns - All available columns.
 * @param {boolean} allowFullReorder - If true, all columns are treated as orderable.
 * @returns {Array} Ordered list of column objects with `checked` property.
 */
const buildViewColumns = (fieldIds, selectableColumns, allowFullReorder) => {
  const selectedSet = new Set(fieldIds || []);
  const colMap = new Map(selectableColumns.map(col => [col.fieldId, col]));

  let result = [];

  if (allowFullReorder) {
    /**
     * Treat all columns as orderable.
     * NOTE: Reordering non-dynamic columns is currently not supported by TableView.
     */
    const usedFieldIds = new Set();

    for (const fieldId of fieldIds) {
      const col = colMap.get(fieldId);
      if (col) {
        usedFieldIds.add(fieldId);
        result.push({ ...col, checked: true });
      }
    }

    for (const col of selectableColumns) {
      if (!usedFieldIds.has(col.fieldId)) {
        result.push({ ...col, checked: false });
      }
    }
  } else {
    /**
     * Only columns with numeric fieldId are treated as orderable (dynamic columns).
     * This is because TableView does not support reordering of non-dynamic (static) columns.
     */
    const staticCols = [];
    const dynamicSelected = [];
    const dynamicNotSelected = [];

    for (const col of selectableColumns) {
      const fieldId = col.fieldId;
      const isSelected = selectedSet.has(fieldId);
      const isDynamic = Number.isFinite(fieldId);

      if (!isDynamic) {
        // Static column
        staticCols.push({ ...col, checked: isSelected });
      } else if (isSelected) {
        // Dynamic and selected
        dynamicSelected.push({ ...col, checked: true });
      } else {
        // Dynamic and not selected
        dynamicNotSelected.push({ ...col, checked: false });
      }
    }

    result = [
      ...staticCols,
      ...dynamicSelected.sort((a, b) => fieldIds.indexOf(a.fieldId) - fieldIds.indexOf(b.fieldId)),
      ...dynamicNotSelected,
    ];
  }

  return result;
};

/**
 * Transforms an array of project view objects into internal view objects.
 * Assigns temporary negative IDs for new views and parses JSON settings.
 *
 * @param {Array} projectViews - Array of project view objects, possibly including jsonViewSettings and viewId.
 * @returns {{ views: Array, nextId: number }} - Object containing the processed views and the next negative ID to use.
 */
const createInternalViews = (projectViews) => {
  let nextId = -1;
  let views = [];

  if (Array.isArray(projectViews) && projectViews.length > 0) {
    views = projectViews.map((projectView) => {
      const { jsonViewSettings } = projectView;

      let view = { ...projectView };

      if (view.viewId <= nextId) {
        nextId = view.viewId - 1;
      }

      if (jsonViewSettings) {
        try {
          const settings = JSON.parse(jsonViewSettings);
          view = { ...view, settings };
        } catch (error) {
          console.error(`Error parsing settings for projectView: ${view}`, error);
        }
      }

      delete view.jsonViewSettings;
      delete view.__typename;

      return view;
    });
  }

  return { views, nextId };
};

/**
 * NameField
 *
 * Controlled input component for editing the name of a project view.
 * Always reflects the user's input visually, but only emits a valid name
 * (containing at least one non-whitespace character), or an empty string if invalid.
 *
 * @param {Object} props
 * @param {(name: string) => void} props.onChange - Callback with a valid name or "" if invalid.
 * @param {Object} props.view - The view object containing at least `name`, `type`, and `viewId`.
 * @returns {JSX.Element}
 */
const NameField = ({ onChange, view }) => {
  const intl = useIntl();

  const [inputValue, setInputValue] = useState(view.name || "");

  useEffect(() => {
    setInputValue(view.name || "");
  }, [view.name]);

  const createLabel = () => {
    if (isValidName(inputValue)) {
      return inputValue;
    }

    const key = ProjectViewUtils.getTranslationKey(view.type);

    return key
      ? intl.formatMessage({ id: key })
      : `(${view.viewId})`;
  };

  const handleClear = () => {
    setInputValue("");
    onChange?.("");
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    onChange?.(isValidName(newValue) ? newValue : "");
  };

  const isValidName = (name) => /\S/.test(name);

  return (
    <TextField
      endAdornment={
        inputValue && (
          <InputAdornment position="end" sx={{ mr: 0.5 }}>
            <IconButton size="small" onClick={handleClear}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        )
      }
      label={intl.formatMessage({ id: "common.name" })}
      onChange={handleInputChange}
      placeholder={createLabel()}
      shrink
      sx={{ "& .MuiInputBase-root": { pr: 0 } }}
      value={inputValue}
    />
  );
};

/**
 * ProjectViewsModal React component.
 *
 * Provides a modal interface for managing project views within a project management application.
 * Users can add new views from available view types, reorder selected views via drag-and-drop,
 * select a view for editing, and delete views.
 *
 * Supports editing of view properties such as name and view-specific settings.
 * Includes detailed editing panels for Kanban and Task views with specialized configuration components.
 *
 * The modal manages internal state for views and tracks deleted views separately before confirming changes.
 *
 * @param {Object} props - Component props.
 * @param {Array} props.initialViews - Initial array of project views, each with metadata and settings.
 * @param {boolean} props.open - Flag indicating whether the modal is open.
 * @param {function} props.onCancel - Callback invoked when the modal is closed without saving.
 * @param {function} props.onUpdate - Callback invoked with updated views when changes are confirmed.
 * @param {Array} props.projectFields - Array of project fields available for view configuration.
 *
 * @returns {JSX.Element|null} The rendered ProjectViewsModal component or null if initial data is not loaded.
 */
const ProjectViewsModal = ({
  initialViews,
  open,
  onCancel,
  onUpdate,
  projectFields,
  selectedViewId,
}) => {
  const intl = useIntl();

  const pendingSelectionIdRef = useRef(selectedViewId);

  const [deletedViews, setDeletedViews] = useState([]);
  const [internalViews, setInternalViews] = useState();
  const [nextViewId, setNextViewId] = useState(-1);
  const [selectedView, setSelectedView] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [viewColumns, setViewColumns] = useState([]);

  const statusFields = useMemo(() => {
    if (!Array.isArray(projectFields))
      return [];

    return projectFields
      .filter(field => field.type === ColumnTypes.STATUS)
      .map(statusField => {
        const { fieldId, jsonFieldSettings, name } = statusField;

        let field = {
          fieldId,
          name,
          settings: [],
        }

        if (jsonFieldSettings) {
          try {
            field.settings = JSON.parse(jsonFieldSettings);
          } catch (error) {
            console.error(`Error parsing settings for projectField: ${statusField}`, error);
          }
        }

        return field;
      });
  }, [projectFields]);

  const selectableColumns = useMemo(() => {
    const standardFields = createStandardColumns(intl).filter(n => n.fieldId !== StandardColumnIds.TASK_NAME);

    return Array.isArray(projectFields)
      ? [
        ...standardFields.map(n => ({ fieldId: n.fieldId, name: n.label, type: undefined })),
        ...projectFields.map(n => ({ fieldId: n.fieldId, name: n.name, type: n.type })),
      ]
      : standardFields;
  }, [intl, projectFields]);

  const handleAdd = (type) => {
    const view = {
      viewId: nextViewId,
      name: '',
      type,
      settings: {},
    };

    setInternalViews((prev) => ([...prev, view]));
    setNextViewId(prev => --prev);

    pendingSelectionIdRef.current = view.viewId; // Wait and select after render
  };

  const handleDelete = () => {
    const { viewId } = selectedView;

    if (viewId > 0) {
      setDeletedViews((prev) => ([...prev, viewId]));
    }

    setInternalViews((prev) => (
      prev.filter((view) => view.viewId !== viewId)
    ));

    setSelectedView(null);
  };

  const handleMoveView = (fromIndex, toIndex) => {
    setInternalViews((prev) => {
      const temp = cloneDeep(prev);
      const [moved] = temp.splice(fromIndex, 1);
      temp.splice(toIndex, 0, moved);
      return temp;
    });
  };

  /**
   * Handles selecting a view by its ID.
   * Updates the selected view and visible columns based on the view's settings.
   *
   * Memoized to avoid unnecessary re-creation and allow safe use in useEffect
   * 
   * @param {string|number} viewId - The ID of the view to select.
   */
  const handleSelectView = useCallback((viewId) => {
    const viewData = (internalViews || []).find(view => view.viewId === viewId);
    setSelectedView(viewData);

    if (!viewData || viewData.type !== ProjectViewType.Tasks) {
      setViewColumns([]);
      return;
    }

    const selectedFieldIds = viewData.settings.fieldIds || [];

    /**
     * Flag indicating if TableView supports full column reordering (currently false).
     * TODO: Fix TableView column reordering.
     */
    const TABLEVIEW_ALLOWS_FULL_COLUMN_REORDERING = false;

    const viewCols = buildViewColumns(
      selectedFieldIds,
      selectableColumns,
      TABLEVIEW_ALLOWS_FULL_COLUMN_REORDERING
    );

    setViewColumns(viewCols);
  }, [internalViews, selectableColumns, setSelectedView, setViewColumns]);

  const handleUpdate = (confirmed) => {
    if (confirmed || deletedViews.length === 0) {
      onUpdate?.(internalViews.map(n => ({
        viewId: n.viewId,
        name: n.name,
        type: n.type,
        jsonViewSettings: JSON.stringify(n.settings),
      })));
    }
    else {
      setShowConfirmDialog(true);
    }
  };

  const updateName = (name) => {
    const { viewId } = selectedView;

    setInternalViews((prev) => {
      const index = prev.findIndex((view) => view.viewId === viewId);
      if (index === -1) {
        return prev;
      }
      const temp = cloneDeep(prev);
      temp[index].name = name;
      return temp;
    });

    setSelectedView((prev) => ({ ...prev, name }));
  };

  const handleChangeKanbanField = useCallback((fieldId) => {
    const { viewId } = selectedView;

    setInternalViews((prev) => {
      const index = prev.findIndex((view) => view.viewId === viewId);
      if (index === -1) {
        return prev;
      }
      const temp = cloneDeep(prev);
      temp[index].settings.fieldId = fieldId;
      return temp;
    });

    setSelectedView((prev) => ({ ...prev, settings: { fieldId } }));
  }, [selectedView]);

  const updateInternalViews = useCallback((updatedColumns) => {
    const { viewId } = selectedView;
    setInternalViews(prev => {
      const index = prev.findIndex(n => n.viewId === viewId);
      if (index === -1) {
        return prev;
      }
      const temp = cloneDeep(prev);
      temp[index].settings.fieldIds = updatedColumns.filter(n => n.checked).map(n => n.fieldId);
      return temp;
    });
  }, [selectedView]);

  const handleMoveTaskColumn = useCallback((fromIndex, index) => {
    setViewColumns(prev => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(index, 0, moved);
      updateInternalViews(updated);
      return updated;
    });
  }, [updateInternalViews]);

  const handleToggleTaskColumn = useCallback((fieldId, checked) => {
    setViewColumns(prev => {
      const updated = prev.map(col =>
        col.fieldId === fieldId ? { ...col, checked } : col
      );
      updateInternalViews(updated);
      return updated;
    });
  }, [updateInternalViews]);

  useEffect(() => {
    const { views, nextId } = createInternalViews(initialViews);
    setInternalViews(views);
    setNextViewId(nextId);
  }, [initialViews]);

  useEffect(() => {
    const id = pendingSelectionIdRef.current;
    if (id !== null && internalViews?.some(v => v.viewId === id)) {
      handleSelectView(id);
      pendingSelectionIdRef.current = null;
    }
  }, [internalViews, handleSelectView]);

  const ActionButtons = ({ isDirty, onClose, onConfirm }) => (
    <Stack direction="row">
      <GrayButton marginRight={8} disabled={false} onClick={onClose}>
        <FormattedMessage id="btn.close" />
      </GrayButton>

      <PrimaryButton variant="contained" disabled={!isDirty} size="small" onClick={onConfirm}>
        <FormattedMessage id="btn.confirm" />
      </PrimaryButton>
    </Stack>
  );

  const DeleteButton = ({ onClick }) => (
    <IconButton onClick={onClick} color="error" sx={{ color: "error.dark", display: "flex", mt: -1.5, top: "6px" }}>
      <DeleteIcon />
    </IconButton>
  );

  const sxPaper = { p: 2, height: '100%', overflowY: 'auto' };

  return internalViews && (
    <>
      <Dialog open={open} onClose={onCancel} fullWidth={true} maxWidth="lg">
        <DialogTitle>
          <FormattedMessage id="project.editViews" />
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={1}>
            <Grid item xs={4}>

              <Paper elevation={3} sx={sxPaper}>
                <Typography variant="h6">
                  <FormattedMessage id="project.availableViews" />
                </Typography>
                <AvailableViews onAdd={handleAdd} />
              </Paper>

            </Grid>
            <Grid item xs={4}>

              <Paper elevation={3} sx={sxPaper}>
                <Typography variant="h6">
                  <FormattedMessage id="project.selectedViews" />
                </Typography>
                <SelectedViews
                  onMove={handleMoveView}
                  onSelect={handleSelectView}
                  projectViews={internalViews}
                  selectedViewId={selectedView?.viewId}
                />
              </Paper>

            </Grid>
            {selectedView && (
              <Grid item xs={4}>

                <Paper elevation={3} sx={sxPaper}>
                  <Stack direction="row" sx={{ position: "relative" }}>
                    <Typography variant="h6">
                      <FormattedMessage id="project.viewDetails" />
                    </Typography>
                    <DeleteButton onClick={handleDelete} />
                  </Stack>
                  <Stack direction="column" sx={{ mb: 2 }}>
                    <NameField onChange={updateName} view={selectedView} />
                    {(selectedView.type === ProjectViewType.Kanban) &&
                      <KanbanViewEditor
                        onChange={handleChangeKanbanField}
                        statusFields={statusFields}
                        view={selectedView}
                      />
                    }
                    {(selectedView.type === ProjectViewType.Tasks) &&
                      <TaskColumnsEditor
                        columns={viewColumns}
                        onMove={handleMoveTaskColumn}
                        onChange={handleToggleTaskColumn}
                      />
                    }
                  </Stack>
                </Paper>

              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1 }}>
          {/* TODO: Implement proper dirty-check. Currently always assumes data is dirty. */}
          <ActionButtons
            isDirty={true}
            onClose={onCancel}
            onConfirm={handleUpdate}
          />
        </DialogActions>
      </Dialog >

      <ConfirmModal
        messageText={<FormattedMessage id="project.confirmDeleteView.message" />}
        onCancel={() => {
          setShowConfirmDialog(false);
        }}
        onConfirm={() => {
          setShowConfirmDialog(false);
          handleUpdate(true);
        }}
        open={showConfirmDialog}
        titleText={<FormattedMessage id="project.confirmDeleteView.title" />}
      />
    </>
  );
};

ProjectViewsModal.propTypes = {
  initialViews: PropTypes.arrayOf(
    PropTypes.shape({
      viewId: PropTypes.number.isRequired,
      name: PropTypes.string,
      type: PropTypes.number.isRequired,
      settings: PropTypes.object,
      jsonViewSettings: PropTypes.string,
    })
  ).isRequired,
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  projectFields: PropTypes.arrayOf(
    PropTypes.shape({
      fieldId: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      type: PropTypes.number.isRequired,
      jsonFieldSettings: PropTypes.string,
    })
  ).isRequired,
};

export default ProjectViewsModal;
